import React, {ChangeEvent, FC, useState} from 'react';
import {useMutation} from '@apollo/client';
import {useLocation} from '@reach/router';
import {BSButton, BSInput} from 'bodyspec-shared-components';
import classNames from 'classnames';
import {EMAIL_REGEX} from 'constants/index';
import {onSubscribeMutation} from 'graphql/mutations';

import HumansIcon from './svgs/humans.inline.svg';

import * as styles from './styles.module.scss';

interface BlogSignUpProps {
	square?: boolean;
	responsive?: boolean;
}

enum SubState {
	ready = 'Ready',
	pending = 'Pending',
	done = 'Done',
}

export const BlogSignUp: FC<BlogSignUpProps> = ({square, responsive}) => {
	const [state, setState] = useState<SubState>(SubState.ready);
	const [email, setEmail] = useState<string>('');
	const location = useLocation();

	// For now, skip error reporting - not super important
	// for just doing very simple email lead capture
	const [subscribe] = useMutation(onSubscribeMutation, {
		onCompleted: () => setState(SubState.done),
		onError: () => setState(SubState.done),
	});

	const handleEmail = (e: ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value);
	};

	const handleSubmit = () => {
		subscribe({
			variables: {
				email,
				path: location.pathname,
			},
		});
		setState(SubState.pending);
	};

	const circle = (color: string, position: string): string => {
		return classNames({
			[styles.circle]: true,
			[styles.blue]: color === 'blue',
			[styles.green]: color === 'green',
			[styles.top]: position === 'top',
			[styles.bottom]: position === 'bottom',
		});
	};

	const title = () => {
		switch (state) {
		case SubState.done:
			return <>
				You're in! Keep an eye on your inbox for news,
				tips, and more to support you on your health journey.
			</>;
		default:
			return <>
				Sign up for our newsletter!<br />
				Get weekly updates.
			</>;
		}
	};

	const buttonProps = {
		disabled: state !== SubState.ready || !email.match(EMAIL_REGEX),
		onClick: handleSubmit,
	};

	return (
		<div className={classNames({
			[styles.wrapper]: true,
			[styles.square]: square,
			[styles.responsive]: responsive,
			[styles.done]: state === SubState.done,
		})}>
			<span className={circle('blue', 'top')}></span>
			<span className={circle('green', 'top')}></span>
			<span className={circle('blue', 'bottom')}></span>
			<span className={circle('green', 'bottom')}></span>
			{square && <HumansIcon />}
			<div className={styles.title}>
				{title()}
			</div>
			{state !== SubState.done &&
				<div className={styles.inputs}>
					<BSInput placeholder='Email' type='email' onChange={handleEmail} />
					<BSButton {...buttonProps}>Subscribe</BSButton>
				</div>
			}
		</div>
	);
};
